"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { fr } from "date-fns/locale";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams } from "next/navigation";

// Define simplified type for conversation
type Conversation = {
    id: string; // key
    otherUser: {
        id: string;
        name: string | null;
        image: string | null;
    };
    listing: {
        id: string;
        title: string;
        images: { url: string }[];
    } | null;
    lastMessage: {
        content: string;
        createdAt: string;
        read: boolean;
        isOwn: boolean;
    };
    unreadCount: number;
};

export function ConversationList() {
    const [conversations, setConversations] = useState<Conversation[]>([]);
    const [loading, setLoading] = useState(true);
    const searchParams = useSearchParams();
    const currentTo = searchParams.get("to");
    const currentListing = searchParams.get("listing");

    useEffect(() => {
        fetch("/api/messages/conversations")
            .then((r) => r.json())
            .then((data) => {
                if (Array.isArray(data)) setConversations(data);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, []);

    if (loading) return <ConversationsSkeleton />;

    if (conversations.length === 0) {
        return (
            <div className="text-center p-8 text-muted-foreground">
                <p>Aucune conversation pour le moment.</p>
            </div>
        );
    }

    return (
        <div className="space-y-2 overflow-y-auto h-full pr-2">
            {conversations.map((conv) => {
                const isActive =
                    (currentTo === conv.otherUser.id && (!conv.listing || currentListing === conv.listing.id)) ||
                    (!currentTo && false); // TODO: handle default selection logic if needed

                // Construct link
                const href = `/messages?to=${conv.otherUser.id}${conv.listing ? `&listing=${conv.listing.id}` : ""}`;

                return (
                    <Link
                        key={conv.id}
                        href={href}
                        className={cn(
                            "flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-muted/50",
                            isActive ? "bg-muted" : "bg-transparent"
                        )}
                    >
                        <Avatar className="h-10 w-10 border border-border">
                            <AvatarImage src={conv.otherUser.image || undefined} />
                            <AvatarFallback>{conv.otherUser.name?.[0]?.toUpperCase() || "?"}</AvatarFallback>
                        </Avatar>

                        <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-baseline mb-1">
                                <h4 className="text-sm font-semibold truncate text-foreground">
                                    {conv.otherUser.name || "Utilisateur"}
                                </h4>
                                <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                                    {formatDistanceToNow(new Date(conv.lastMessage.createdAt), {
                                        addSuffix: false,
                                        locale: fr,
                                    })}
                                </span>
                            </div>

                            {conv.listing && (
                                <p className="text-xs text-primary/80 font-medium truncate mb-0.5">
                                    {conv.listing.title}
                                </p>
                            )}

                            <p className={cn(
                                "text-xs truncate",
                                conv.unreadCount > 0 ? "font-bold text-foreground" : "text-muted-foreground"
                            )}>
                                {conv.lastMessage.isOwn ? "Vous: " : ""}{conv.lastMessage.content}
                            </p>
                        </div>

                        {conv.unreadCount > 0 && (
                            <div className="flex flex-col items-center justify-center">
                                <span className="h-5 w-5 rounded-full bg-primary text-[10px] font-bold text-primary-foreground flex items-center justify-center">
                                    {conv.unreadCount}
                                </span>
                            </div>
                        )}
                    </Link>
                );
            })}
        </div>
    );
}

function ConversationsSkeleton() {
    return (
        <div className="space-y-4 p-2">
            {[1, 2, 3].map((i) => (
                <div key={i} className="flex gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-3 w-full" />
                    </div>
                </div>
            ))}
        </div>
    );
}

